/**
 * Rejestr zestawów sekcji kokpitu decyzji („Grupuj według: Kartoteka").
 *
 * Sekcja kartoteki to tabela z kolumnami LISTY tej kartoteki (te same pola „pokaż na liście" z
 * katalogu pól co na jej własnej stronie) + przypięta kolumna „Decyzja". Zestaw sekcji mówi, JAK
 * zbudować te kolumny dla danej kartoteki: który moduł systemowy dostarcza katalog pól i układ
 * listy, jakie nadpisania renderu / wykluczenia / remap sortu / kolumny towarzyszące obowiązują.
 * Wszystko to żyje we wspólnej konfiguracji kolumn kartoteki (`lib/documents/*-columns.tsx`,
 * `lib/contractors/contractor-columns.tsx`) i stamtąd jest importowane — strona kartoteki i sekcja
 * nie mają własnych kopii, więc lista i sekcja nie rozjadą się.
 *
 * Zestawy mają: siedem klas dokumentów (faktury zakupu i sprzedaży, umowy, pisma, dokumenty handlowe,
 * wewnętrzne i specjalne — klucz grupy `documents:<klasa>`) oraz kontrahenci (klucz grupy = kod celu
 * `crm_core.contractors`). Dokumenty Symfonii Handel mają osobny, STAŁY zestaw bez katalogu pól
 * (`StaticSectionKit`, rejestr w `handel-section-kits.tsx`), a kartoteki własne — zestaw z
 * katalogiem dostarczanym w odpowiedzi sekcji (`CustomSectionKit`, `custom-section-kit.tsx`).
 * Klucz grupy bez żadnego zestawu (nie powinien się zdarzyć) zostaje zwijaną grupą wspólnej tabeli.
 *
 * Zestaw mówi też, które kolumny mają sumę „Razem" pod tabelą (`sumSlugs`): faktury — Netto, VAT,
 * Brutto; umowy — „Wartość"; pozostałe kartoteki bez sum.
 *
 * Kolejność kolumn sekcji = kolejność kolumn strony listy: rdzenne z nadpisaniami, firma, kategoria,
 * odwołania dokumentu (opiekun, dla 5 klas „standardowych" także źródło), kolumny klasy (satelita
 * umowy, nadawca pisma), kolumny procesu, pola dodatkowe. Kontrahenci nie mają na liście odwołań
 * dokumentu ani kolumn procesu — sekcja ich nie dokłada.
 */

import type { TFunction } from 'i18next';

import type { ColumnDefinition } from '@/components/configurable-table';
import {
  buildContractorOverrides,
  CONTRACTOR_ALWAYS_SLUGS,
  CONTRACTOR_COLUMN_COMPANIONS,
  CONTRACTOR_COLUMN_ID_TO_SLUG,
  CONTRACTOR_COMPANY_COLUMN_ARGS,
  type ContractorRow,
  CONTRACTORS_EXCLUDE_SLUGS,
} from '@/lib/contractors/contractor-columns';
import {
  buildContractDetailColumns,
  CONTRACT_ALWAYS_SLUGS,
  CONTRACT_COLUMN_COMPANIONS,
  CONTRACT_COLUMN_ID_TO_SLUG,
  CONTRACT_OVERRIDES,
  CONTRACT_PROCESS_LABEL_PREFIX,
  type ContractRow,
  CONTRACTS_EXCLUDE_SLUGS,
  CONTRACTS_SORT_SLUG_OVERRIDES,
} from '@/lib/documents/contract-columns';
import { buildDocumentCoreRefColumns } from '@/lib/documents/document-core-ref-columns';
import {
  buildLetterSenderColumn,
  LETTER_ALWAYS_SLUGS,
  LETTER_COLUMN_COMPANIONS,
  LETTER_COLUMN_ID_TO_SLUG,
  LETTER_PROCESS_LABEL_PREFIX,
  type LetterRow,
  LETTERS_EXCLUDE_SLUGS,
  LETTERS_OVERRIDES,
  LETTERS_SORT_SLUG_OVERRIDES,
} from '@/lib/documents/letter-columns';
import { buildProcessColumns, type ProcessColumnsRow } from '@/lib/documents/process-columns';
import {
  type InvoiceRow,
  PI_ALWAYS_SLUGS,
  PI_COLUMN_COMPANIONS,
  PI_COLUMN_ID_TO_SLUG,
  PI_EXCLUDE_SLUGS,
  PI_PROCESS_LABEL_PREFIX,
  PI_SORT_SLUG_OVERRIDES,
  PURCHASE_INVOICE_OVERRIDES,
} from '@/lib/documents/purchase-invoice-columns';
import {
  SALE_INVOICE_OVERRIDES,
  type SaleInvoiceRow,
  SI_ALWAYS_SLUGS,
  SI_COLUMN_COMPANIONS,
  SI_COLUMN_ID_TO_SLUG,
  SI_EXCLUDE_SLUGS,
  SI_PROCESS_LABEL_PREFIX,
  SI_SORT_SLUG_OVERRIDES,
} from '@/lib/documents/sale-invoice-columns';
import {
  COMMERCIAL_PROCESS_LABEL_PREFIX,
  type CommercialDocRow,
  INTERNAL_PROCESS_LABEL_PREFIX,
  type InternalDocRow,
  SPECIAL_PROCESS_LABEL_PREFIX,
  type SpecialDocRow,
  STANDARD_DOC_ALWAYS_SLUGS,
  STANDARD_DOC_COLUMN_ID_TO_SLUG,
  STANDARD_DOC_EXCLUDE_SLUGS,
  STANDARD_DOC_OVERRIDES,
} from '@/lib/documents/standard-document-columns';
import {
  buildCategoryColumn,
  buildCompanyColumn,
  buildCoreColumns,
  buildExtColumns,
  type ColumnCompanionRule,
  type CoreColumnOverride,
  type ExtRefLabels,
  type SystemModuleFieldDef,
} from '@/lib/ext-list-columns';
import {
  EMPTY_LIST_FILTER_OPTIONS,
  type ListFilterOptions,
  listFilterSkipSlugs,
} from '@/lib/list-filter-options';
import type { MyPendingDecisionRow } from '@/lib/processes/approvals-api';
import { CONTRACTOR_MODULE_CODE, DOCUMENTS_MODULE_CODE } from '@/lib/processes/module-routes';

/**
 * Wiersz sekcji — to, co tabela sekcji czyta niezależnie od kartoteki: id rekordu i kroki decyzji
 * czekające na użytkownika (jeden rekord może mieć kilka otwartych kroków). Pozostałe pola wiersza
 * to wiersz listy danej kartoteki (np. `InvoiceRow`, `ContractRow`) — czytają je wyłącznie kolumny
 * zestawu, który je zbudował (patrz rzut typów w `buildSectionColumns`). Typ celowo NIE ma sygnatury
 * indeksu: osłabiałaby typowanie wszystkich zestawów (każdy klucz jako `unknown`). Zestaw kartoteki
 * własnej, której wiersz to wartości pól pod slugami, buduje kolumny na własnym typie wiersza
 * (`CustomSectionRow` w `custom-section-kit.tsx`).
 */
export interface DecisionSectionRow {
  id: string;
  decisions: MyPendingDecisionRow[];
}

/** Wejście budowy kolumn sekcji — wszystko, co zależy od stanu komponentu (katalog, tłumaczenia, meta serwera). */
export interface SectionColumnsContext {
  /** Katalog pól modułu (`GET /system-modules/:slug/fields`) — źródło kolumn rdzennych i dodatkowych. */
  fields: SystemModuleFieldDef[];
  t: TFunction;
  /** Stabilny getter etykiet pól-odwołań (patrz `useExtRefLabelsGetter`). */
  getRefLabels: () => ExtRefLabels | undefined;
  /**
   * Firmy REKORDÓW sekcji (z serwera, `companyOptions`) — opcje filtra kolumny „Firma". To nie są firmy
   * użytkownika: lista zawiera tylko te firmy, których rekordy są w sekcji.
   */
  companies: ReadonlyArray<{ id: string; name: string }>;
  /** Czy kolumna SYSTEMOWA jest sortowalna serwerowo (`meta.sortable` ostatniej odpowiedzi). */
  isSortable: (slug: string) => boolean;
  /**
   * Czy kolumna SYSTEMOWA jest filtrowalna serwerowo (`meta.filterable` ostatniej odpowiedzi). Pyta się
   * po SLUGU BACKENDU (bez remapu sortu) — np. okres umowy po `period_from`.
   */
  isFilterable: (slug: string) => boolean;
  /**
   * Opcje list wyboru w wierszu filtrów, które trzeba wczytać (pozycje słowników pól `select`, użytkownicy
   * dla „Opiekuna") — `useListFilterOptions`. Pominięte = bez nich (kolumny bez opcji mają pustą komórkę
   * filtra, nigdy martwe pole).
   */
  filterOptions?: ListFilterOptions;
  /** Tekst zamiast surowego id dla odwołania, którego serwer nie rozwiązał („(brak dostępu)"). */
  unresolvedRefLabel: string;
}

/** Wynik budowy kolumn: definicje + mapowanie id kolumny → slug backendu dla `?columns=`. */
export interface SectionColumns {
  columns: ColumnDefinition<DecisionSectionRow>[];
  /** id kolumny rdzennej → slug backendu (id = slug wszędzie poza dedykowanymi kolumnami spoza katalogu). */
  coreIdToSlug: Record<string, string>;
  /** Slugi kolumn dodatkowych (id kolumny ext = slug pola). */
  extSlugSet: Set<string>;
}

export interface DecisionSectionKit {
  /** Rozróżnik zestawów: z katalogu pól (ten plik) albo stały (`handel-section-kits.tsx`). */
  source: 'catalog';
  /** Slug modułu systemowego: katalog pól i układ listy kartoteki. */
  moduleSlug: string;
  /**
   * Kod celu procesu rekordów tej kartoteki (`targetModuleCode` wiersza decyzji) — z nim sekcja
   * woła nawigację do karty rekordu (klik wiersza, „Otwórz formularz"). Dla dokumentów to jeden
   * kod `crm_core.documents` (klasę dokumentu rozwiązuje nawigacja po id), dla kontrahentów
   * `crm_core.contractors`.
   */
  targetModuleCode: string;
  /** Remap id kolumny → slug sortu serwera (`?sort=`), np. dostawca sortuje się po nazwie. */
  sortSlugOverrides: Readonly<Record<string, string>>;
  /** Slugi dołączane do `?columns=` zawsze (zależności logiki akcji wiersza). */
  alwaysSlugs: readonly string[];
  /** Reguły towarzysza dla `?columns=` (kolumna złożona czyta dodatkowe pola). */
  companions: readonly ColumnCompanionRule[];
  /**
   * Slugi pól rysowanych nadpisaniem kolumny albo osobnym builderem (klucze rejestru nadpisań +
   * slugi wykluczone z generyka) — dla nich panel nie pobiera słowników opcji filtra
   * (`useListFilterOptions`), bo generyk ich nie użyje.
   */
  filterOptionsSkipSlugs: ReadonlySet<string>;
  /**
   * Id kolumn kwot z sumą „Razem" pod tabelą (widok „Lista") — równe slugom sum z serwera (`sums=1`).
   * Pusta lista = sekcja bez kwot: panel nie prosi o sumy i nie rysuje stopki.
   */
  sumSlugs: readonly string[];
  buildColumns: (ctx: SectionColumnsContext) => SectionColumns;
}

/** Wejście budowy kolumn STAŁEGO zestawu sekcji (bez katalogu pól) — to, co zależy od stanu komponentu. */
export interface StaticSectionColumnsContext {
  t: TFunction;
  /** Firmy rekordów sekcji (z serwera, `companyOptions`) — opcje filtra „Firma". */
  companies: ReadonlyArray<{ id: string; name: string }>;
  /** Czy kolumna jest sortowalna serwerowo (`meta.sortable` ostatniej odpowiedzi; pyta po id kolumny, z remapem sortu). */
  isSortable: (columnId: string) => boolean;
  /** Czy kolumna jest filtrowalna serwerowo (`meta.filterable` ostatniej odpowiedzi; pyta po SLUGU backendu). */
  isFilterable: (slug: string) => boolean;
}

/**
 * Zestaw sekcji o STAŁYCH kolumnach — bez katalogu pól, układu administratora i pól dodatkowych
 * (dokumenty Symfonii Handel: wiersze nie pochodzą z naszej bazy, więc nie ma czego konfigurować).
 * Zapytanie sekcji nie niesie `columns`, a podpowiedzi wartości filtra (`section/distinct`) nie
 * istnieją — kolumny filtrują warunkiem albo listą stałych opcji.
 */
export interface StaticSectionKit {
  source: 'static';
  /** Kod celu procesu — z nim sekcja woła nawigację do dokumentu (klik wiersza, „Otwórz formularz"). */
  targetModuleCode: string;
  /** Remap id kolumny → slug sortu serwera (`?sort=`); kolumny bez wpisu sortują po własnym id. */
  sortSlugOverrides: Readonly<Record<string, string>>;
  /** Początkowy sort sekcji (kolumna + kierunek) — ten sam, który stosuje serwer bez `sort`. */
  defaultSort: { column: string; direction: 'asc' | 'desc' };
  /** Id kolumn kwot z sumą „Razem" (= slugi sum z serwera). */
  sumSlugs: readonly string[];
  buildColumns: (ctx: StaticSectionColumnsContext) => ColumnDefinition<DecisionSectionRow>[];
}

/**
 * Zestaw sekcji KARTOTEKI WŁASNEJ (kartoteka z kreatora, klucz grupy `custom.<slug>`): bez
 * katalogu pól i układu pobieranych osobno — oba przychodzą w odpowiedzi samej sekcji
 * (`columns`), bo decydujący może nie mieć prawa do kartoteki. Kolumny i wiersz filtrów buduje
 * wspólny builder listy kartoteki własnej (`lib/custom-modules/module-list-columns.tsx`), z
 * kodem w `custom-section-kit.tsx`. Zestaw jest tworzony z klucza grupy (`getCustomSectionKit`),
 * nie ma rejestru — kartotek własnych jest tyle, ile tenant ich utworzył — ale ten sam klucz daje
 * zawsze ten sam obiekt (cache w `getCustomSectionKit`).
 */
export interface CustomSectionKit {
  source: 'custom';
  /** Kod celu procesu = klucz grupy (`custom.<slug>`); z nim nawigacja otwiera kartę rekordu. */
  targetModuleCode: string;
}

/** Zestaw sekcji dowolnego rodzaju — z katalogu pól, stały albo kartoteki własnej. */
export type SectionKit = DecisionSectionKit | StaticSectionKit | CustomSectionKit;

/** Minimalny kształt wiersza listy kartoteki, który zestaw potrafi zbudować w kolumny. */
interface SectionBaseRow {
  id: string;
  ext?: Record<string, unknown>;
}

/** Argumenty fabryki kolumn klasy dokumentu (satelita umowy, nadawca pisma). */
interface ClassColumnsArgs {
  t: (key: string) => string;
  isSortable: (slug: string) => boolean;
  isFilterable: (slug: string) => boolean;
}

/** Argumenty fabryki kolumn procesu (patrz `buildProcessColumns`) — bez filtrowalności (filtr kolumn procesu jest zawsze warunkiem). */
interface ProcessColumnsArgs extends Omit<ClassColumnsArgs, 'isFilterable'> {
  isProcessModuleActive: boolean;
}

/**
 * Opis kolumn sekcji jednej kartoteki — odpowiednik tego, co jej strona listy składa w `columns`.
 * Każde pole pochodzi z jej wspólnej konfiguracji kolumn; `Row` to typ wiersza LISTY kartoteki.
 */
interface SectionColumnsSpec<Row extends SectionBaseRow> {
  /** Rejestr nadpisań renderu kolumn rdzennych. */
  overrides: Readonly<Record<string, CoreColumnOverride<Row>>>;
  /** Slugi rdzenne obsługiwane osobno (dedykowane buildery). */
  excludeSlugs: ReadonlySet<string>;
  /** Część stała mapy id kolumny → slug backendu (kolumny z katalogu dopisuje budowa pętlą id = slug). */
  columnIdToSlug: Readonly<Record<string, string>>;
  /** Argumenty kolumny „Firma", gdy firma rekordu żyje w innym polu niż `company_id` (kontrahenci). */
  companyColumnArgs?: { readonly accessor: string; readonly fieldSlug: string };
  /**
   * Odwołania dokumentu: opiekun (+ źródło dla `withSource`). Brak = kartoteka bez nich na liście
   * (kontrahenci); faktury renderują źródło osobnym nadpisaniem (`withSource: false`).
   */
  documentRefs?: { readonly withSource: boolean };
  /** Kolumny klasy dokumentu (satelita umowy, nadawca pisma) — po odwołaniach dokumentu. */
  classColumns?: (args: ClassColumnsArgs) => ColumnDefinition<Row>[];
  /** Kolumny procesu. Brak = kartoteka bez nich na liście (kontrahenci). */
  processColumns?: (args: ProcessColumnsArgs) => ColumnDefinition<Row>[];
}

/**
 * Buduje kolumny sekcji w kolejności strony listy kartoteki (patrz nagłówek pliku). Filtry nagłówka
 * są TE SAME co na liście (flagę `conditionOnly` mają tylko te kolumny, które mają ją na liście,
 * np. kolumny procesu i kategoria). Zakładka „Wartości" popupu filtra bierze podpowiedzi z rekordów
 * SAMEJ sekcji (`distinctTableId` tabeli sekcji → `section/distinct`), nie z całej kartoteki.
 * Moduł procesów jest w sekcji z definicji aktywny (każdy wiersz ma otwarty krok), więc kolumny
 * procesu są widoczne domyślnie.
 */
function buildSectionColumns<Row extends SectionBaseRow>(
  ctx: SectionColumnsContext,
  spec: SectionColumnsSpec<Row>,
): SectionColumns {
  const { fields, t, getRefLabels, companies, isSortable, isFilterable, unresolvedRefLabel } = ctx;
  const { selectOptions, ownerOptions } = ctx.filterOptions ?? EMPTY_LIST_FILTER_OPTIONS;

  const extColumns = buildExtColumns<Row>(fields, t, getRefLabels, unresolvedRefLabel);
  const coreColumns = buildCoreColumns<Row>({
    fields,
    t,
    getRefLabels,
    overrides: spec.overrides,
    excludeSlugs: spec.excludeSlugs,
    resolveSortable: (f) => isSortable(f.slug),
    resolveFilterable: (f) => isFilterable(f.slug),
    selectOptions,
    unresolvedRefLabel,
  });

  const documentRefColumns = spec.documentRefs
    ? buildDocumentCoreRefColumns<Row>({
        t,
        getRefLabels,
        unresolvedRefLabel,
        resolveOwnerFilterable: isFilterable,
        ownerFilterOptions: ownerOptions,
        ...(spec.documentRefs.withSource
          ? {
              withSource: true,
              resolveSourceSortable: isSortable,
              resolveSourceFilterable: isFilterable,
            }
          : {}),
      })
    : [];

  const columns: ColumnDefinition<Row>[] = [
    ...coreColumns,
    buildCompanyColumn<Row>({ companies, getRefLabels, t, ...spec.companyColumnArgs }),
    buildCategoryColumn<Row>({ t, resolveSortable: isSortable }),
    ...documentRefColumns,
    ...(spec.classColumns?.({ t, isSortable, isFilterable }) ?? []),
    ...(spec.processColumns?.({ t, isSortable, isProcessModuleActive: true }) ?? []),
    ...extColumns,
  ];

  // Mapa id kolumny → slug backendu dla `?columns=` (jak na stronie kartoteki).
  const coreIdToSlug: Record<string, string> = { ...spec.columnIdToSlug };
  for (const c of coreColumns) coreIdToSlug[c.id] = c.id; // id = slug (buildCoreColumns)

  return {
    // JEDYNY rzut typów w zestawach — granica rejestru: kolumny są zbudowane na typie wiersza listy
    // kartoteki (`Row`), a rejestr trzyma je pod wspólnym typem sekcji. Bezpieczne, bo wiersze sekcji
    // mają kształt wiersza listy tej kartoteki (ten sam deskryptor po stronie serwera) + `decisions`,
    // a tabela sekcji czyta z wiersza wyłącznie `id` i `decisions`; resztę pól czytają kolumny `Row`.
    columns: columns as unknown as ColumnDefinition<DecisionSectionRow>[],
    coreIdToSlug,
    extSlugSet: new Set(extColumns.map((c) => c.id)),
  };
}

/** Składa zestaw sekcji z opisu kolumn typowanego wierszem listy kartoteki (`Row`). */
function defineSectionKit<Row extends SectionBaseRow>(definition: {
  moduleSlug: string;
  targetModuleCode: string;
  sortSlugOverrides: Readonly<Record<string, string>>;
  alwaysSlugs: readonly string[];
  companions: readonly ColumnCompanionRule[];
  /** Id kolumn z sumą „Razem"; pominięte = sekcja bez kwot (bez sum i stopki). */
  sumSlugs?: readonly string[];
  columns: SectionColumnsSpec<Row>;
}): DecisionSectionKit {
  const { columns, sumSlugs = NO_SUM_SLUGS, ...kit } = definition;
  return {
    source: 'catalog',
    ...kit,
    filterOptionsSkipSlugs: listFilterSkipSlugs(columns.overrides, columns.excludeSlugs),
    sumSlugs,
    buildColumns: (ctx) => buildSectionColumns<Row>(ctx, columns),
  };
}

/** Fabryka kolumn procesu z prefiksem kluczy i18n kartoteki (patrz `buildProcessColumns`). */
function processColumnsWithPrefix<Row extends ProcessColumnsRow>(
  labelKeyPrefix: string,
): (args: ProcessColumnsArgs) => ColumnDefinition<Row>[] {
  return (args) => buildProcessColumns<Row>({ ...args, labelKeyPrefix });
}

/**
 * Brak remapu sortu — kartoteka, której lista go nie ma (id kolumny = slug sortu serwera). Wspólny
 * dla zestawów z katalogu pól i dla kartoteki własnej (`DecisionSectionTable`).
 */
export const NO_SORT_SLUG_OVERRIDES: Readonly<Record<string, string>> = {};
/** Brak reguł towarzysza — kartoteka, której lista ich nie ma. */
const NO_COMPANIONS: readonly ColumnCompanionRule[] = [];

/** Sekcja bez kolumn kwot — bez sum „Razem". Wspólna dla wszystkich rodzajów zestawów. */
export const NO_SUM_SLUGS: readonly string[] = [];
/** Faktury (zakupu i sprzedaży): Netto, VAT, Brutto — id kolumny = slug sumy serwera. */
const INVOICE_SUM_SLUGS: readonly string[] = ['amount_net', 'amount_vat', 'amount_gross'];
/** Umowy: „Wartość" (id kolumny satelity umowy = slug sumy serwera). */
const CONTRACT_SUM_SLUGS: readonly string[] = ['contract_value'];

/**
 * Faktury zakupu — kolumny jak na liście `PurchaseInvoices` (rdzenne z nadpisaniami, firma,
 * kategoria, opiekun, „Aktywny proces" i „Bieżący etap", pola dodatkowe). Źródło faktur renderuje
 * nadpisanie (plakietka), nie wspólna kolumna źródła.
 */
const PURCHASE_INVOICE_SECTION_KIT = defineSectionKit<InvoiceRow>({
  moduleSlug: 'purchase_invoices',
  targetModuleCode: DOCUMENTS_MODULE_CODE,
  sortSlugOverrides: PI_SORT_SLUG_OVERRIDES,
  alwaysSlugs: PI_ALWAYS_SLUGS,
  companions: PI_COLUMN_COMPANIONS,
  sumSlugs: INVOICE_SUM_SLUGS,
  columns: {
    overrides: PURCHASE_INVOICE_OVERRIDES,
    excludeSlugs: PI_EXCLUDE_SLUGS,
    columnIdToSlug: PI_COLUMN_ID_TO_SLUG,
    documentRefs: { withSource: false },
    processColumns: processColumnsWithPrefix<InvoiceRow>(PI_PROCESS_LABEL_PREFIX),
  },
});

/** Faktury sprzedaży — kolumny jak na liście `SaleInvoices` (jak faktury zakupu; „Nabywca" zamiast „Dostawcy"). */
const SALE_INVOICE_SECTION_KIT = defineSectionKit<SaleInvoiceRow>({
  moduleSlug: 'sale_invoices',
  targetModuleCode: DOCUMENTS_MODULE_CODE,
  sortSlugOverrides: SI_SORT_SLUG_OVERRIDES,
  alwaysSlugs: SI_ALWAYS_SLUGS,
  companions: SI_COLUMN_COMPANIONS,
  sumSlugs: INVOICE_SUM_SLUGS,
  columns: {
    overrides: SALE_INVOICE_OVERRIDES,
    excludeSlugs: SI_EXCLUDE_SLUGS,
    columnIdToSlug: SI_COLUMN_ID_TO_SLUG,
    documentRefs: { withSource: false },
    processColumns: processColumnsWithPrefix<SaleInvoiceRow>(SI_PROCESS_LABEL_PREFIX),
  },
});

/**
 * Umowy — kolumny jak na liście `Contracts`: rdzenne z nadpisaniami, firma, kategoria, opiekun i
 * źródło, kolumny satelity umowy (numer, status, okres, wartość, strona główna), kolumny procesu,
 * pola dodatkowe.
 */
const CONTRACT_SECTION_KIT = defineSectionKit<ContractRow>({
  moduleSlug: 'contracts',
  targetModuleCode: DOCUMENTS_MODULE_CODE,
  sortSlugOverrides: CONTRACTS_SORT_SLUG_OVERRIDES,
  alwaysSlugs: CONTRACT_ALWAYS_SLUGS,
  companions: CONTRACT_COLUMN_COMPANIONS,
  sumSlugs: CONTRACT_SUM_SLUGS,
  columns: {
    overrides: CONTRACT_OVERRIDES,
    excludeSlugs: CONTRACTS_EXCLUDE_SLUGS,
    columnIdToSlug: CONTRACT_COLUMN_ID_TO_SLUG,
    documentRefs: { withSource: true },
    classColumns: (args) => buildContractDetailColumns(args),
    processColumns: processColumnsWithPrefix<ContractRow>(CONTRACT_PROCESS_LABEL_PREFIX),
  },
});

/**
 * Pisma — kolumny jak na liście `Letters`: rdzenne z nadpisaniami, firma, kategoria, opiekun i
 * źródło, kolumna „Nadawca", kolumny procesu, pola dodatkowe.
 */
const LETTER_SECTION_KIT = defineSectionKit<LetterRow>({
  moduleSlug: 'letters',
  targetModuleCode: DOCUMENTS_MODULE_CODE,
  sortSlugOverrides: LETTERS_SORT_SLUG_OVERRIDES,
  alwaysSlugs: LETTER_ALWAYS_SLUGS,
  companions: LETTER_COLUMN_COMPANIONS,
  columns: {
    overrides: LETTERS_OVERRIDES,
    excludeSlugs: LETTERS_EXCLUDE_SLUGS,
    columnIdToSlug: LETTER_COLUMN_ID_TO_SLUG,
    documentRefs: { withSource: true },
    classColumns: (args) => [buildLetterSenderColumn(args)],
    processColumns: processColumnsWithPrefix<LetterRow>(LETTER_PROCESS_LABEL_PREFIX),
  },
});

/**
 * Dokumenty handlowe — kolumny jak na liście `CommercialDocs`: rdzenne z nadpisaniami, firma,
 * kategoria, opiekun i źródło, kolumny procesu, pola dodatkowe.
 */
const COMMERCIAL_SECTION_KIT = defineSectionKit<CommercialDocRow>({
  moduleSlug: 'commercial',
  targetModuleCode: DOCUMENTS_MODULE_CODE,
  sortSlugOverrides: NO_SORT_SLUG_OVERRIDES,
  alwaysSlugs: STANDARD_DOC_ALWAYS_SLUGS,
  companions: NO_COMPANIONS,
  columns: {
    overrides: STANDARD_DOC_OVERRIDES,
    excludeSlugs: STANDARD_DOC_EXCLUDE_SLUGS,
    columnIdToSlug: STANDARD_DOC_COLUMN_ID_TO_SLUG,
    documentRefs: { withSource: true },
    processColumns: processColumnsWithPrefix<CommercialDocRow>(COMMERCIAL_PROCESS_LABEL_PREFIX),
  },
});

/**
 * Dokumenty wewnętrzne — kolumny jak na liście `InternalDocs` (te same co w dokumentach handlowych,
 * poza prefiksem kluczy i18n kolumn procesu).
 */
const INTERNAL_SECTION_KIT = defineSectionKit<InternalDocRow>({
  moduleSlug: 'internal',
  targetModuleCode: DOCUMENTS_MODULE_CODE,
  sortSlugOverrides: NO_SORT_SLUG_OVERRIDES,
  alwaysSlugs: STANDARD_DOC_ALWAYS_SLUGS,
  companions: NO_COMPANIONS,
  columns: {
    overrides: STANDARD_DOC_OVERRIDES,
    excludeSlugs: STANDARD_DOC_EXCLUDE_SLUGS,
    columnIdToSlug: STANDARD_DOC_COLUMN_ID_TO_SLUG,
    documentRefs: { withSource: true },
    processColumns: processColumnsWithPrefix<InternalDocRow>(INTERNAL_PROCESS_LABEL_PREFIX),
  },
});

/**
 * Dokumenty specjalne — kolumny jak na liście `SpecialDocs` (te same co w dokumentach handlowych i
 * wewnętrznych, poza prefiksem kluczy i18n kolumn procesu). Elementy tylko tej listy (kalendarz, panel
 * struktury organizacyjnej, zawężenie do typu dokumentu) nie dotyczą tabeli sekcji.
 */
const SPECIAL_SECTION_KIT = defineSectionKit<SpecialDocRow>({
  moduleSlug: 'specjalne',
  targetModuleCode: DOCUMENTS_MODULE_CODE,
  sortSlugOverrides: NO_SORT_SLUG_OVERRIDES,
  alwaysSlugs: STANDARD_DOC_ALWAYS_SLUGS,
  companions: NO_COMPANIONS,
  columns: {
    overrides: STANDARD_DOC_OVERRIDES,
    excludeSlugs: STANDARD_DOC_EXCLUDE_SLUGS,
    columnIdToSlug: STANDARD_DOC_COLUMN_ID_TO_SLUG,
    documentRefs: { withSource: true },
    processColumns: processColumnsWithPrefix<SpecialDocRow>(SPECIAL_PROCESS_LABEL_PREFIX),
  },
});

/**
 * Kontrahenci — kolumny jak na liście `Contractors`: rdzenne z nadpisaniami (nazwa z podpisem i
 * linkiem do karty, typ podmiotu, status jako plakietka), firma macierzysta, kategoria, pola
 * dodatkowe. Bez kolumn procesu i odwołań dokumentu (lista kontrahentów ich nie ma). Link w nazwie
 * nie dokleja pozycji menu (`withMenuItem` = tożsamość): sekcja kokpitu nie jest wejściem z pozycji
 * menu kartoteki, a bez aktywnej pozycji menu hook `useWithMenuItem` i tak zwraca ścieżkę bez zmian.
 */
const CONTRACTOR_SECTION_KIT = defineSectionKit<ContractorRow>({
  moduleSlug: 'contractors',
  targetModuleCode: CONTRACTOR_MODULE_CODE,
  sortSlugOverrides: NO_SORT_SLUG_OVERRIDES,
  alwaysSlugs: CONTRACTOR_ALWAYS_SLUGS,
  companions: CONTRACTOR_COLUMN_COMPANIONS,
  columns: {
    overrides: buildContractorOverrides((path) => path),
    excludeSlugs: CONTRACTORS_EXCLUDE_SLUGS,
    columnIdToSlug: CONTRACTOR_COLUMN_ID_TO_SLUG,
    companyColumnArgs: CONTRACTOR_COMPANY_COLUMN_ARGS,
  },
});

/** Zestawy sekcji wg klucza grupy z nagłówka koszyka (`DecisionGroupHeader.key`). */
export const SECTION_KITS: Readonly<Record<string, DecisionSectionKit>> = {
  'documents:purchase_invoice': PURCHASE_INVOICE_SECTION_KIT,
  'documents:sale_invoice': SALE_INVOICE_SECTION_KIT,
  'documents:contract': CONTRACT_SECTION_KIT,
  'documents:letter': LETTER_SECTION_KIT,
  'documents:commercial': COMMERCIAL_SECTION_KIT,
  'documents:internal': INTERNAL_SECTION_KIT,
  'documents:specjalne': SPECIAL_SECTION_KIT,
  [CONTRACTOR_MODULE_CODE]: CONTRACTOR_SECTION_KIT,
};

/** Zestaw sekcji dla klucza grupy albo `undefined` (kartoteka bez sekcji — zostaje zwijaną grupą tabeli). */
export function getSectionKit(groupKey: string): DecisionSectionKit | undefined {
  // `Object.hasOwn` — klucz grupy to dane z serwera; `in`/indeksowanie wprost trafiłoby
  // w odziedziczone nazwy z `Object.prototype`.
  return Object.hasOwn(SECTION_KITS, groupKey) ? SECTION_KITS[groupKey] : undefined;
}
